<script>
import { getContrastingTextColor } from '@chatwoot/utils';

const ARM_GOLD_LABEL_TRANSLATIONS = {
  'agent-approved-quote': 'اعتمد العرض',
  'agent-rejected-quote': 'رفض العرض',
  'ai-auto-evaluated': 'تم تقييم AI',
  'ai-eval-error': 'خطأ تقييم AI',
  'ai-quote-blocked': 'عرض AI موقوف',
  'ai-quote-draft-ready': 'مسودة AI جاهزة',
  b2b: 'شركات / B2B',
  booking_inquiry: 'استفسار حجز',
  'bot-fu-23h': 'متابعة 23 ساعة',
  'bot-fu-30m': 'متابعة 30 دقيقة',
  'bot-fu-4h': 'متابعة 4 ساعات',
  'bot-fu-8h': 'متابعة 8 ساعات',
  'bot-got-city': 'تم تحديد المدينة',
  'bot-got-dates': 'تم تحديد التواريخ',
  'bot-got-guests': 'تم تحديد الأشخاص',
  'bot-got-hotel': 'تم تحديد الفندق',
  'bot-got-rooms': 'تم تحديد الغرف',
  'bot-qualified': 'مؤهل آلياً',
  'bot-welcome': 'ترحيب أرسل',
  'cold-lead': 'عميل بارد',
  'competitor-check-error': 'خطأ فحص منافسين',
  'competitor-checked': 'تم فحص المنافسين',
  'competitor-manual-required': 'فحص منافسين يدوي',
  'hot-lead': 'عميل ساخن',
  'human-required': 'يحتاج موظف',
  'manual-price-needed': 'تسعير يدوي',
  'need-competitor-check': 'يحتاج فحص منافسين',
  'needs-agent-approval': 'يحتاج اعتماد موظف',
  'needs-manual-price': 'يحتاج سعر يدوي',
  'needs-reprice': 'يحتاج إعادة تسعير',
  'not-booking': 'ليس طلب حجز',
  'outside-24h-template-required': 'يحتاج قالب واتساب',
  'quote-approved-sent': 'العرض المعتمد أرسل',
  ramadan_bookings_2026: 'حجوزات رمضان 2026',
  ramadan_vip: 'رمضان VIP',
  ramdan_vip2: 'رمضان VIP 2',
  'sla-agent-missed': 'تأخر رد الموظف',
  'sla-ai-rescue': 'إنقاذ AI للـ SLA',
  spam_irrelevant: 'سبام / غير مناسب',
  'vendor-error': 'خطأ مورد',
  'vendor-login-needed': 'يحتاج دخول مورد',
  'warm-lead': 'عميل دافئ',
  'waiting-customer': 'بانتظار العميل',
  'waiting-supplier': 'بانتظار المورد',
  vip: 'VIP',
};

export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    bgColor: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    colorScheme: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: '',
    },
  },
  emits: ['remove'],
  computed: {
    textColor() {
      if (this.variant === 'smooth') return '';
      if (this.variant === 'dashed') return '';
      return this.color || getContrastingTextColor(this.bgColor);
    },
    labelClass() {
      return `label ${this.colorScheme} ${this.variant} ${
        this.small ? 'small' : ''
      }`;
    },
    labelStyle() {
      if (this.bgColor) {
        return {
          background: this.bgColor,
          color: this.textColor,
          border: `1px solid ${this.bgColor}`,
        };
      }
      return {};
    },
    anchorStyle() {
      if (this.bgColor) {
        return { color: this.textColor };
      }
      return {};
    },
    displayTitle() {
      return ARM_GOLD_LABEL_TRANSLATIONS[this.title] || this.title;
    },
  },
  methods: {
    onClick() {
      this.$emit('remove', this.title);
    },
  },
};
</script>

<template>
  <div
    class="inline-flex ltr:mr-1 rtl:ml-1 mb-1"
    :class="labelClass"
    :style="labelStyle"
    :title="description"
  >
    <span v-if="icon" class="label-action--button">
      <fluent-icon :icon="icon" size="12" class="label--icon cursor-pointer" />
    </span>
    <span
      v-if="['smooth', 'dashed'].includes(variant) && title && !icon"
      :style="{ background: color }"
      class="label-color-dot flex-shrink-0"
    />
    <span v-if="!href" class="whitespace-nowrap text-ellipsis overflow-hidden">
      {{ displayTitle }}
    </span>
    <a v-else :href="href" :style="anchorStyle">{{ displayTitle }}</a>
    <button
      v-if="showClose"
      class="label-close--button p-0"
      :style="{ color: textColor }"
      @click="onClick"
    >
      <fluent-icon icon="dismiss" size="12" class="close--icon" />
    </button>
  </div>
</template>

<style scoped lang="scss">
.label {
  @apply items-center font-medium text-xs rounded-[4px] gap-1 p-1 bg-n-slate-3 text-n-slate-12 border border-solid border-n-strong h-6;

  &.small {
    @apply text-xs py-0.5 px-1 leading-tight h-5;
  }

  &.small .label--icon,
  &.small .close--icon {
    @apply text-[0.5rem];
  }

  a {
    @apply text-xs;
    &:hover {
      @apply underline;
    }
  }

  /* Color Schemes */
  &.primary {
    @apply bg-n-blue-5 text-n-blue-12 border border-solid border-n-blue-7;

    a {
      @apply text-n-blue-12;
    }
    .label-color-dot {
      @apply bg-n-blue-9;
    }
  }
  &.secondary {
    @apply bg-n-slate-5 text-n-slate-12 border border-solid border-n-slate-7;

    a {
      @apply text-n-slate-12;
    }
    .label-color-dot {
      @apply bg-n-slate-9;
    }
  }
  &.success {
    @apply bg-n-teal-5 text-n-teal-12 border border-solid border-n-teal-7;

    a {
      @apply text-n-teal-12;
    }
    .label-color-dot {
      @apply bg-n-teal-9;
    }
  }
  &.alert {
    @apply bg-n-ruby-5 text-n-ruby-12 border border-solid border-n-ruby-7;

    a {
      @apply text-n-ruby-12;
    }
    .label-color-dot {
      @apply bg-n-ruby-9;
    }
  }
  &.warning {
    @apply bg-n-amber-5 text-n-amber-12 border border-solid border-n-amber-7;

    a {
      @apply text-n-amber-12;
    }
    .label-color-dot {
      @apply bg-n-amber-9;
    }
  }

  &.smooth {
    @apply bg-transparent text-n-slate-11 dark:text-n-slate-12 border border-solid border-n-strong;
  }

  &.dashed {
    @apply bg-transparent text-n-slate-11 dark:text-n-slate-12 border border-dashed border-n-strong;
  }
}

.label-close--button {
  @apply text-n-slate-11 -mb-0.5 rounded-sm cursor-pointer flex items-center justify-center hover:bg-n-slate-3;

  svg {
    @apply text-n-slate-11;
  }
}

.label-action--button {
  @apply flex mr-1;
}

.label-color-dot {
  @apply inline-block w-3 h-3 rounded-sm shadow-sm;
}
.label.small .label-color-dot {
  @apply w-2 h-2 rounded-sm shadow-sm;
}
</style>
